import {useState} from 'react';
import type {ReferenceBlock as RefBlockType, ReferenceBlockField, ReferenceBlockType} from '../types/vault';
import {copyBlockText, copyToClipboard} from '../utils/clipboard';
import {config} from '../data/config';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCopy, FiEye, FiEyeOff, FiEdit2, FiCheck, FiPlus, FiTrash2} = FiIcons;

interface ReferenceBlockProps {
  block: RefBlockType;
  onEdit?: () => void;
}

export function ReferenceBlock({block, onEdit}: ReferenceBlockProps) {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (text: string, id: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleCopyBlock = () => {
    const fieldsForCopy = block.fields.map(f => ({
      label: f.label,
      value: f.sensitive && !revealed[f.id] ? '••••••••' : f.value,
    }));
    const text = copyBlockText(block.label, fieldsForCopy);
    handleCopy(text, 'block');
  };

  const toggleReveal = (id: string) => {
    setRevealed(prev => ({...prev, [id]: !prev[id]}));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{block.label}</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopyBlock}
            className="p-1.5 text-slate-400 hover:text-[#0F766E] hover:bg-white rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
            aria-label="Copy entire block"
          >
            <SafeIcon icon={copiedId === 'block' ? FiCheck : FiCopy} className="w-4 h-4" />
          </button>
          {onEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 text-slate-400 hover:text-[#0F766E] hover:bg-white rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
              aria-label="Edit block"
            >
              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">
        {block.fields.sort((a, b) => a.sortOrder - b.sortOrder).map(field => {
          const isRevealed = revealed[field.id];
          const displayValue = field.sensitive && !isRevealed ? '••••••••' : field.value;
          return (
            <div key={field.id} className="flex items-start justify-between group">
              <div className="flex-1 min-w-0 pr-3">
                <p className="text-xs text-slate-500 mb-0.5">{field.label}</p>
                <p className="text-sm text-slate-900 font-medium break-all">{displayValue}</p>
              </div>
              <div className="flex items-center gap-1">
                {field.sensitive && (
                  <button
                    onClick={() => toggleReveal(field.id)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                    aria-label={isRevealed ? 'Hide sensitive value' : 'Reveal sensitive value'}
                  >
                    <SafeIcon icon={isRevealed ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => handleCopy(displayValue, field.id)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                  aria-label={`Copy ${field.label}`}
                >
                  <SafeIcon icon={copiedId === field.id ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ReferenceBlockFormProps {
  block?: RefBlockType | null;
  profileId: string;
  onSave: (block: RefBlockType) => void;
  onCancel: () => void;
}

export function ReferenceBlockForm({block, profileId, onSave, onCancel}: ReferenceBlockFormProps) {
  const [label, setLabel] = useState(block?.label ?? '');
  const [blockType, setBlockType] = useState<ReferenceBlockType>(block?.blockType ?? 'Custom');
  const [fields, setFields] = useState<ReferenceBlockField[]>(
    block?.fields.length
      ? [...block.fields]
      : [{
          id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          label: '',
          value: '',
          sensitive: false,
          sortOrder: 1,
        }]
  );

  const addField = () => {
    setFields(prev => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        label: '',
        value: '',
        sensitive: false,
        sortOrder: prev.length + 1,
      },
    ]);
  };

  const removeField = (id: string) => {
    setFields(prev => prev.filter(f => f.id !== id));
  };

  const updateField = (id: string, updates: Partial<ReferenceBlockField>) => {
    setFields(prev => prev.map(f => (f.id === id ? {...f, ...updates} : f)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || fields.length === 0) return;
    const now = new Date().toISOString();
    onSave({
      id: block?.id ?? '',
      profileId,
      blockType,
      label: label.trim(),
      fields: fields.map((f, i) => ({...f, sortOrder: i + 1})),
      archived: block?.archived ?? false,
      created: block?.created ?? now,
      updated: now,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Block Label <span className="text-red-500">*</span>
          </label>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            required
            placeholder="e.g. Tax Details"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={blockType}
            onChange={e => setBlockType(e.target.value as ReferenceBlockType)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
          >
            {config.referenceBlockTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium text-slate-700">
            Fields <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={addField}
            className="inline-flex items-center px-3 py-2 min-h-[44px] text-sm text-[#0F766E] hover:text-[#0B2138] font-medium rounded-lg hover:bg-[#0F766E]/5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1.5" />
            Add Field
          </button>
        </div>

        <div className="space-y-3">
          {fields.map(field => (
            <div
              key={field.id}
              className="bg-slate-50 p-3 rounded-lg border border-slate-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Label</label>
                  <input
                    value={field.label}
                    onChange={e => updateField(field.id, {label: e.target.value})}
                    placeholder="e.g. TFN"
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Value</label>
                  <input
                    value={field.value}
                    onChange={e => updateField(field.id, {value: e.target.value})}
                    placeholder="Value"
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center text-xs text-slate-600 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.sensitive}
                    onChange={e => updateField(field.id, {sensitive: e.target.checked})}
                    className="mr-2 rounded border-slate-300 text-[#0F766E] focus:ring-[#0F766E]"
                  />
                  Sensitive value (mask by default)
                </label>
                <button
                  type="button"
                  onClick={() => removeField(field.id)}
                  disabled={fields.length === 1}
                  className="w-11 h-11 shrink-0 flex items-center justify-center text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-red-300 transition-colors"
                  aria-label={`Remove field ${field.label || 'unnamed'}`}
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto px-4 py-2 min-h-[44px] text-sm text-slate-600 hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-full sm:w-auto px-4 py-2 min-h-[44px] text-sm bg-[#102D4C] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors"
        >
          {block ? 'Save Changes' : 'Add Block'}
        </button>
      </div>
    </form>
  );
}